import { CloseIcon } from '@chakra-ui/icons'
import { Box, Button, Center, Flex, HStack, Text } from '@chakra-ui/react'
import { PopUpMessageContext, usePopUpMessage } from '@hb/react'
import { AssessmentDocumentArgs, Template, UpdateCallback, WithId } from '@hb/shared'
import React, { useCallback, useContext, useEffect, useState } from 'react'
import { sendAssessmentDocument } from '../../../hooks/backend/documents/utils'
import { useFile } from '../../../hooks/backend/storage/downloadFile'
import { LoadingOverlay } from '../../LoadingOverlay'
import { PdfView } from '../../PdfView'
import { SubmitText } from '../../RichText'
import { useTemplateData, useTemplateView } from '../contexts'
import { EditorCoverageSelect } from '../EditorCoverageSelect'
import { PdfViewSubmitButton } from '../EditorSubmitButton'
import { TemplateMenu } from '../TemplateMenu/TemplateMenu'
import { ExternalPdfTemplateEditorProvider, useExternalPdfTemplateEditor } from './context'

const ExternalPdfViewHeader = ({
  onBack,
  // templateType,
  // template,
  submitText,
}: {
  onBack?: () => void
  // templateType: TemplateKey
  // template: WithId<Template> | null
  submitText: SubmitText | undefined
}) => {
  const {
    shortcutArgs: { assessmentId, assessment, user, plan },
  } = useTemplateData()
  const {
    type: templateType,
    template: { data: template },
    templateId,
    setManualTemplateId,
    onSubmit,
  } = useTemplateView()
  const { showError } = usePopUpMessage()

  const { processResponse } = useContext(PopUpMessageContext)
  const [submitLoading, setSubmitLoading] = useState(false)
  const handleSubmit = useCallback(
    async (documentName: string): Promise<UpdateCallback> => {
      if (!templateId) return processResponse({ error: 'No templateId' })

      // practice contracts submit through the provider's onSubmit (no assessment/patient
      // context); an empty text payload makes the server take the external-PDF copy branch
      if (templateType === 'practiceContract') {
        if (!onSubmit) return processResponse({ error: 'No submit handler' })
        setSubmitLoading(true)
        const res = await onSubmit(
          [],
          template?.editorVersion ?? 'v2',
          templateId,
          documentName,
          null,
        )
        setSubmitLoading(false)
        return processResponse(res)
      }

      const patientId = assessment?.patientId
      if (!assessmentId) return processResponse({ error: 'No assessmentId' })
      if (!patientId) return processResponse({ error: 'No patientId' })

      const args: AssessmentDocumentArgs = {
        assessmentId,
        patientId,
        type: templateType,
        text: [],
        templateId,
        name: documentName,
        coverageId: plan?.id ?? null,
      }
      setSubmitLoading(true)
      try {
        await sendAssessmentDocument({ document: args })
        setSubmitLoading(false)
        return processResponse({ success: 'Document sent' })
      } catch (e: any) {
        setSubmitLoading(false)
        showError(e.message)
        console.error(e)
        return processResponse({ error: e.message || 'Failed to send document' })
      }
    },
    [
      assessment,
      templateType,
      assessmentId,
      processResponse,
      plan,
      showError,
      templateId,
      onSubmit,
      template,
    ],
  )

  return (
    <HStack
      borderBottom="1px solid #cdcdcd"
      align="center"
      width="100%"
      p={2}
      bg="gray.50"
      zIndex={4}>
      <Box minW="0" flex={1}>
        <TemplateMenu
          insuranceProviderId={user?.insuranceProvider?.id ?? null}
          onChange={t =>
            setManualTemplateId({ templateId: t.template.id, practiceId: t.practiceId })
          }
          templateId={templateId}
          templateType={templateType}
        />
      </Box>
      {templateType === 'practiceContract' ? null : <EditorCoverageSelect />}
      <PdfViewSubmitButton
        template={template}
        templateType={templateType}
        submitLoading={submitLoading}
        handleSubmit={handleSubmit}
        submitText={submitText}
      />
      <Flex gap={1}>
        {onBack ? (
          <Button
            variant="outline"
            onClick={onBack}
            size="xs"
            bg="gray.50"
            color="gray.500"
            alignItems="center"
            gap={1}>
            <CloseIcon position="relative" top="1px" w={2} />
            <Text>Close</Text>
          </Button>
        ) : null}
      </Flex>
    </HStack>
  )
}

const ExternalPdfTemplateViewBody = ({
  width,
  modalWidth,
  height,
  header,
  // manualTemplateSelectedOn,
  template,
  submitText,
  // templateType,
  // setManualTemplateId,
  onClose,
}: {
  width: number
  modalWidth?: number
  height: number
  // manualTemplateSelectedOn: React.RefObject<number>
  template: WithId<Template> | null
  // templateType: TemplateKey
  // setManualTemplateId: (id: string) => void
  header?: React.ReactNode | null
  submitText?: SubmitText
  onClose?: () => void
}) => {
  const { readOnly } = useTemplateView()
  const { externalPdf } = useExternalPdfTemplateEditor()
  // const { externalPdf } = template ?? {}
  const { url } = useFile({ path: externalPdf?.baseFile.storagePath })

  if (!template || !externalPdf) {
    return (
      <Center w={`${modalWidth ?? width}px`} h={`${height}px`}>
        <Box>External PDF not found</Box>
      </Center>
    )
  }
  return (
    <Box overflow="hidden" width={`${modalWidth ?? width}px`} height={`${height}px`}>
      <PdfView
        // editable={!readOnly}
        onClose={onClose}
        attachments={externalPdf?.attachments}
        height={height}
        width={width}
        modalWidth={modalWidth}
        url={url}>
        <Box w="100%">
          {header}
          {readOnly ? null : <ExternalPdfViewHeader onBack={onClose} submitText={submitText} />}
        </Box>
      </PdfView>
    </Box>
  )
}

export const ExternalPdfTemplateView = ({
  width,
  height,
  header,
  modalWidth,
  submitText,
  onClose,
}: {
  width: number
  height: number
  modalWidth?: number
  header?: React.ReactNode | null
  submitText?: SubmitText
  onClose?: () => void
}) => {
  const {
    template: { data: template },
  } = useTemplateView()
  const { externalPdf } = template ?? {}

  const [restartingWithTemplate, setRestartingWithTemplate] = useState(false)

  useEffect(() => {
    if (externalPdf) {
      setRestartingWithTemplate(true)
      const t = setTimeout(() => {
        setRestartingWithTemplate(false)
      }, 250)
      return () => clearTimeout(t)
    }
  }, [externalPdf])

  if (restartingWithTemplate) {
    return (
      <Center w={`${modalWidth ?? width}px`} h={`${height}px`}>
        <LoadingOverlay />
      </Center>
    )
  }

  if (!template || !externalPdf) {
    return (
      <Center w={`${modalWidth ?? width}px`} h={`${height}px`}>
        <Box>External PDF not found</Box>
      </Center>
    )
  }
  return (
    <ExternalPdfTemplateEditorProvider template={template} externalPdf={externalPdf}>
      <ExternalPdfTemplateViewBody
        width={width}
        height={height}
        modalWidth={modalWidth}
        header={header}
        template={template}
        submitText={submitText}
        onClose={onClose}
      />
    </ExternalPdfTemplateEditorProvider>
  )
}
